// Timeline data store
import { getStreamBreaks, getStreamNdvr } from "@/api/streams.js";
import { parseValidNumberOrDefault } from "../../utils/parsing";
import { roundToMilliseconds } from "@/utils/time.js";

const START_ZOOM_KEY = "start_zoom";
const START_ZOOM = 0.01;
const MIN_ZOOM = 0.01;
const ZOOM_STEP = 1.2;
const START_SLIDER_DURATION = 60 * 3;
const START_SLIDER_PADDING = 60 * 2;
const TIMELINE_RIGHT_EXTEND = 10 * 60;
// const MAX_TIMELINE_PADDING_LEFT = 10 * 60;
const MIN_TIMELINE_PADDING_RIGHT = 5 * 60;
const MAX_STREAM_LENGTH = 24 * 3600;
const CURSOR_SHAKE_FILTER_RANGE = [-2, 0];
const TIMELINE_STATUS = {
  LOADING: "loading",
  INITED: "inited",
  FAILED: "failed"
};

const savedZoom = localStorage.getItem(START_ZOOM_KEY);

const state = {
  /** timeline status */
  status: TIMELINE_STATUS.LOADING,
  /** scale factor */
  zoom: parseValidNumberOrDefault(savedZoom, START_ZOOM),
  /** timeline overall */
  ndvrStreamStart: null,
  ndvrStreamEnd: null,
  /** timeline calendar */
  ndvrStreamStartCalendar: null,
  ndvrStreamEndCalendar: null,
  /** timeline visible */
  visibleTimelineLeft: null,
  visibleTimelineRight: null,
  activeUpdates: true,
  /** timeline crop controls */
  sliderLeftBound: null,
  sliderRightBound: null,
  /** timeline cursor */
  cursorPosition: null,
  /** Timeline breaks */
  breaks: []
};

const mutations = {
  /** scale */
  setZoom(state, value) {
    if (value <= 0 || value > 1) {
      console.warn("Bad zoom mutation - it must be in range (0-1]");
    }
    state.zoom = value;
  },
  /** set stream begin time */
  setNDVRStreamStart(state, value) {
    state.ndvrStreamStart = roundToMilliseconds(value);
  },
  /** set stream current time */
  setNDVRStreamEnd(state, value) {
    state.ndvrStreamEnd = roundToMilliseconds(value);
  },
  ndvrStreamStartCalendarMut(state, value) {
    state.ndvrStreamStartCalendar = value;
  },
  ndvrStreamEndCalendarMut(state, value) {
    state.ndvrStreamEndCalendar = value;
  },
  setVisibleRange(state, [left, right]) {
    if (left >= right) {
      console.warn("Bad visible range mutation - right is after left");
      return;
    }

    if (right > state.ndvrStreamEnd + TIMELINE_RIGHT_EXTEND) {
      console.warn("Bad visible range mutation - right is after max padding");
      return;
    }

    // if (left < state.ndvrStreamStart - MAX_TIMELINE_PADDING_LEFT) {
    //   console.warn("Bad visible range mutation - left is before max padding");
    //   return;
    // }

    state.visibleTimelineLeft = roundToMilliseconds(left);
    state.visibleTimelineRight = roundToMilliseconds(right);
  },
  /** set new timeline playing cursor position */
  setCursorPosition(state, value) {
    if (value < state.ndvrStreamStart) {
      console.warn("Bad player position mutation - it's before stream start");
      return;
    }

    if (value > state.ndvrStreamEnd) {
      console.warn("Bad player position mutation - it's after stream end");
      return;
    }

    state.cursorPosition = roundToMilliseconds(value);
  },
  /** change trim borders position */
  setSliderRange: (state, [left, right]) => {
    if (left < state.ndvrStreamStart) {
      console.warn("Bad trim range mutation - left is before stream start");
      return;
    }

    if (right > state.ndvrStreamEnd) {
      console.warn("Bad trim range mutation - right is after stream end");
      return;
    }


    state.sliderLeftBound = roundToMilliseconds(left);
    state.sliderRightBound = roundToMilliseconds(right);
  },
  /** set stream breaks array */
  setBreaksList: (state, list) => {
    state.breaks = list;
  },
  /** set timeline loading status */
  setTimelineStatus: (state, status) => {
    if (!Object.values(TIMELINE_STATUS).includes(status)) {
      console.warn(`Bad timeline status mutation - invalid status ${status}`);
      return;
    }

    state.status = status;
  }
};

const getters = {
  /** get scale length */
  visibleTimelineLength: state => {
    return state.visibleTimelineRight - state.visibleTimelineLeft;
  },
  isSliderLeftVisible: (state, getters) => {
    return getters.isVisible(state.sliderLeftBound);
  },
  isSliderRightVisible: (state, getters) => {
    return getters.isVisible(state.sliderRightBound);
  },
  ndvrStreamLength: state => {
    return state.ndvrStreamEnd - state.ndvrStreamStart;
  },
  isTimelineInited: state => {
    return state.status === TIMELINE_STATUS.INITED;
  },
  isTimelineFailed: state => {
    return state.status === TIMELINE_STATUS.FAILED;
  },
  maxVisibleRight: state => {
    return state.ndvrStreamEnd + TIMELINE_RIGHT_EXTEND;
  },
  isVisible: state => {
    return time =>
      state.visibleTimelineLeft <= time && time <= state.visibleTimelineRight;
  }
};

const actions = {
  /** init timeline information */
  async initTimeline({ rootState, state, commit, dispatch, getters }) {
    try {
      commit("setTimelineStatus", TIMELINE_STATUS.LOADING);

      const { app, stream } = rootState.services;
      const { start, end } = await getStreamNdvr(app, stream);

      commit("ndvrStreamStartCalendarMut", start);
      commit("ndvrStreamEndCalendarMut", end);
      commit("setNDVRStreamStart", start);
      commit("setNDVRStreamEnd", end);
      commit("setCursorPosition", end);
      commit("setVisibleRange", [start, getters.maxVisibleRight]);
      dispatch("zoom", { value: state.zoom });

      const sliderStart = Math.max(
        start,
        end - START_SLIDER_PADDING - START_SLIDER_DURATION
      );
      const sliderEnd = Math.min(end, sliderStart + START_SLIDER_DURATION);
      commit("setSliderRange", [sliderStart, sliderEnd]);

      commit("setTimelineStatus", TIMELINE_STATUS.INITED);
    } catch (err) {
      commit("setTimelineStatus", TIMELINE_STATUS.FAILED);
      console.error(err);
    }
  },
  /** update stream information */
  async synchronizeTime({ rootState, state, commit /*, getters*/ }) {
    try {
      if (state.activeUpdates) {
        const { app, stream } = rootState.services;
        const { start, end, recordedEnd } = await getStreamNdvr(app, stream);

        /** update streamend */
        if (state.ndvrStreamEnd !== end) {
          commit("setNDVRStreamEnd", end);
        }

        /** start */
        const minStartTime = end - MAX_STREAM_LENGTH;
        const boundedStart = Math.max(start, minStartTime);

        if (state.ndvrStreamStart !== boundedStart) {
          commit("setNDVRStreamStart", boundedStart);
        }

        /** when we have to update right bound if it's nessesary */
        const hasRightPadding = state.visibleTimelineRight > end;
        const isPaddinTooLong =
          state.visibleTimelineRight - recordedEnd < MIN_TIMELINE_PADDING_RIGHT;

        if (hasRightPadding && isPaddinTooLong) {
          commit("setVisibleRange", [
            state.visibleTimelineLeft,
            recordedEnd + TIMELINE_RIGHT_EXTEND
          ]);
        }
      }

      /** Update left crop bound */
      if (state.sliderLeftBound < state.ndvrStreamStart) {
        commit("setSliderRange", [
          state.ndvrStreamStart,
          state.sliderRightBound
        ]);
      }
    } catch (e) {
      console.warn("Unable to sync time ", e);
    }
  },
  /** change playing cursor position */
  updateCursor({ commit, state }, position) {
    const cursorPosition = position;

    /** smooth time shaking */
    const shakeDetect = cursorPosition - state.cursorPosition;
    const [min, max] = CURSOR_SHAKE_FILTER_RANGE;
    if (shakeDetect > min && shakeDetect < max) {
      return;
    }

    commit("setCursorPosition", cursorPosition);
  },
  /** move start trim border */
  moveSliderLeftBound({ state, commit }, value) {
    commit("setSliderRange", [
      state.sliderLeftBound + value,
      state.sliderRightBound
    ]);
  },
  /** move end trim border */
  moveSliderRightBound({ state, commit }, value) {
    commit("setSliderRange", [
      state.sliderLeftBound,
      state.sliderRightBound + value
    ]);
  },
  moveSliderBoth({ state, commit }, value) {
    commit("setSliderRange", [
      state.sliderLeftBound + value,
      state.sliderRightBound + value
    ]);
  },
  // setEndTime({ commit }, time) {
  //   commit("setNDVRStreamEnd", time);
  // },
  zoomIn({ dispatch, state }, pointer) {
    dispatch("zoom", {
      pointer,
      value: state.zoom / ZOOM_STEP
    });
  },
  zoomOut({ dispatch, state }, pointer) {
    dispatch("zoom", {
      pointer,
      value: state.zoom * ZOOM_STEP
    });
  },
  /** change view area scale */
  zoom({ state, commit, getters }, { value, pointer }) {
    value = Math.min(Math.max(value, MIN_ZOOM), 1);

    commit("setZoom", value);
    localStorage.setItem(START_ZOOM_KEY, value);

    const rightBorder = state.ndvrStreamEnd + TIMELINE_RIGHT_EXTEND;
    const fullLength = getters.ndvrStreamLength + TIMELINE_RIGHT_EXTEND;
    const zoomedLength = value * fullLength;

    const isCursorVisible =
      state.visibleTimelineLeft <= state.cursorPosition &&
      state.cursorPosition <= state.visibleTimelineRight;

    const visibleMiddle =
      (state.visibleTimelineLeft + state.visibleTimelineRight) / 2;

    const preferredMiddle =
      pointer ?? (isCursorVisible ? state.cursorPosition : visibleMiddle);

    const preferredLeft = preferredMiddle - zoomedLength / 2;
    const preferredRight = preferredMiddle + zoomedLength / 2;

    const isBoundedFromLeft = preferredLeft < state.ndvrStreamStart;
    const isBoundedFromRight = preferredRight > rightBorder;

    let actualLeft = null;
    let actualRight = null;

    /** Set zoom have 4 standard cases:
     * 1. Both bounded zoom
     * 2. Right bounded zoom
     * 3. Left bounded zoom
     * 4. Free setting
     */
    if (isBoundedFromLeft && isBoundedFromRight) {
      actualLeft = state.ndvrStreamStart;
      actualRight = rightBorder;
    } else if (isBoundedFromLeft) {
      actualLeft = state.ndvrStreamStart;
      actualRight = actualLeft + zoomedLength;
    } else if (isBoundedFromRight) {
      actualRight = rightBorder;
      actualLeft = actualRight - zoomedLength;
    } else {
      actualLeft = preferredLeft;
      actualRight = preferredRight;
    }

    commit("setVisibleRange", [actualLeft, actualRight]);
  },

  scrollVisibleTimelineForward({ commit, state }, shift) {
    if (shift <= 0) {
      console.warn(`Incorrect value to scroll timeline forward: ${shift}`);
      return;
    }

    const rightTargetValue = state.visibleTimelineRight + shift;
    const leftTargetValue = state.visibleTimelineLeft + shift;
    commit("setVisibleRange", [leftTargetValue, rightTargetValue]);
  },

  scrollVisibleTimelineBackward({ commit, state }, shift) {
    if (shift <= 0) {
      console.warn(`Incorrect value to scroll timeline backward: ${shift}`);
      return;
    }

    const rightTargetValue = state.visibleTimelineRight - shift;
    const leftTargetValue = state.visibleTimelineLeft - shift;
    commit("setVisibleRange", [leftTargetValue, rightTargetValue]);
  },

  async updateBreaks({ rootState, state, commit }) {
    /** update breaks information */
    const breaks = await getStreamBreaks(
      rootState.services.app,
      rootState.services.stream
    );

    if (breaks.length) {
      /** start placeholder for stream that failed before statustics */
      if (!("start" in breaks[0])) {
        breaks[0].start = 0;
      }

      /** end placeholder for stream that is failed now */
      if (!("end" in breaks[breaks.length - 1])) {
        breaks[breaks.length - 1].end = state.ndvrStreamEnd;
      }
    }

    commit("setBreaksList", breaks);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
